import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from '@/store';
import { fetchLookupTables } from '@/services/complianceRequirementsService';
import { hasComplianceReviewRole, isProductAndEngineeringOrg } from '@/utils/roleHelpers';
import { isUserInApprovedOrg } from '@/utils/complianceReviewAccess';
import { env } from '@/config/env';

export const useComplianceReviewAccess = () => {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [canAccessComplianceReview, setCanAccessComplianceReview] = useState(false);
  // Start as true so route guards don't redirect before the first async check completes
  const [isCheckingComplianceReviewAccess, setIsCheckingComplianceReviewAccess] = useState(true);

  const roles = useMemo(() => user?.roles ?? [], [user]);
  const organization = useMemo(() => user?.organizationName ?? "", [user]);
  const userHasReviewRole = useMemo(() => hasComplianceReviewRole(roles), [roles]);
  const userIsProductAndEngineering = useMemo(() => isProductAndEngineeringOrg(organization), [organization]);

  useEffect(() => {
    let isMounted = true;

    // Re-enter the checking state on every run so a prior denial doesn't leave
    // the flag false while a new async org lookup is in flight (which would let
    // route guards redirect prematurely after login / role change).
    setIsCheckingComplianceReviewAccess(true);

    const grant = () => {
      if (!isMounted) return;
      setCanAccessComplianceReview(true);
      setIsCheckingComplianceReviewAccess(false);
    };

    const deny = () => {
      if (!isMounted) return;
      setCanAccessComplianceReview(false);
      setIsCheckingComplianceReviewAccess(false);
    };

    if (!env.authEnabled && !env.authorizationEnabled) {
      // Dev mode with auth disabled — grant without a network call
      grant();
    } else if (!isAuthenticated || !user || !userHasReviewRole) {
      deny();
    } else if (userIsProductAndEngineering) {
      // P&E is not an approving org; its Administrators and Users review across all orgs.
      grant();
    } else {
      const orgName = user.organizationName?.trim();
      if (orgName) {
        fetchLookupTables()
          .then((lookupTables) => {
            if (!isMounted) return;
            const approvedOrgNames = (lookupTables.approvingOrgs ?? []).map((org) => org.name);
            setCanAccessComplianceReview(isUserInApprovedOrg(orgName, approvedOrgNames));
          })
          .catch(() => {
            if (!isMounted) return;
            setCanAccessComplianceReview(false);
          })
          .finally(() => {
            if (!isMounted) return;
            setIsCheckingComplianceReviewAccess(false);
          });
      } else {
        deny();
      }
    }

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user, userHasReviewRole, userIsProductAndEngineering]);

  return {
    canAccessComplianceReview,
    isCheckingComplianceReviewAccess,
  };
};
